import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { AdminSidebar } from '@/components/layout/AdminSidebar'

interface SeriesData {
  id?: number; title?: string; slug?: string; description?: string; cover_url?: string
}

interface Props {
  series: SeriesData | null
  errors: string[]
}

export default function Form({ series, errors }: Props) {
  const isEdit = !!series?.id
  const [form, setForm] = useState({
    title: series?.title ?? '',
    description: series?.description ?? '',
    cover_url: series?.cover_url ?? '',
  })

  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isEdit) {
      router.patch(`/admin/series/${series!.slug}`, form)
    } else {
      router.post('/admin/series', form)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar active="/admin/series" />
      <main className="flex-1 p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-sm text-gray-400 mb-1">
              <Link href="/admin/series" className="hover:underline">Series</Link> › {isEdit ? 'Edit' : 'New'}
            </p>
            <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit series' : 'New series'}</h1>
          </div>
          <div className="flex gap-2">
            <Link href="/admin/series"><Button variant="secondary">Cancel</Button></Link>
            <Button onClick={handleSubmit}>Save series</Button>
          </div>
        </div>

        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            {errors.map((e, i) => <p key={i} className="text-sm text-red-600">{e}</p>)}
          </div>
        )}

        <div className="max-w-xl bg-white rounded-xl border border-gray-100 p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              value={form.title}
              onChange={e => set('title', e.target.value)}
              className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20"
              placeholder="Series title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              rows={4}
              className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 resize-y"
              placeholder="What will learners build in this series?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cover image URL</label>
            <input
              value={form.cover_url}
              onChange={e => set('cover_url', e.target.value)}
              className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 font-mono"
              placeholder="https://r2.getcasts.io/covers/..."
            />
            {form.cover_url && (
              <img src={form.cover_url} alt="cover preview" className="mt-2 rounded-lg h-24 object-cover" />
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
